import React, { useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { sectorsData } from '../data/sectorsData';
import { serviceSectors } from '../data/serviceSectors';
import { Heading } from '../components/Heading';
import { theme } from '../styles/GlobalStyles';

// --- Styled Components ---

const SectorHero = styled.section`
  position: relative;
  width: 100vw;
  min-height: clamp(60vh, 70vh, 80vh);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const SectorHeroBg = styled.div<{ bgImage: string }>`
  position: absolute;
  inset: 0;
  z-index: 0;
  background: url('${props => props.bgImage}') center/cover no-repeat;
  filter: brightness(0.6) blur(0.5px);
  animation: heroEffect 20s ease-in-out infinite alternate;
  will-change: transform;
  
  @keyframes heroEffect {
    0% { transform: scale(1) translate(0, 0); }
    100% { transform: scale(1.05) translate(-1%, -1%); }
  }
`;

const SectorHeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.5) 100%);
  z-index: 1;
`;

const SectorHeroContent = styled.div`
  position: relative;
  z-index: 2;
  text-align: center;
  color: #fff;
  max-width: 900px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const SectorTitle = styled.h1`
  font-family: 'Playfair Display', serif;
  font-size: clamp(2.5rem, 6vw, 4.5rem);
  font-weight: 900;
  color: ${theme.colors.accent};
  text-shadow: 0 4px 32px #000a, 0 2px 8px ${theme.colors.accent}55;
  margin-bottom: 1rem;
  line-height: 1.2;
`;

const SectorSubtitle = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: clamp(1.1rem, 2vw, 1.4rem);
  font-weight: 400;
  color: #fff;
  text-shadow: 0 2px 16px #000a;
  line-height: 1.5;
`;

const ContentSection = styled.section`
  background: ${theme.colors.sectionAlt};
  width: 100%;
  padding: clamp(4rem, 8vw, 6rem) 0;
`;

const ContentContainer = styled.div`
  max-width: min(1200px, 95vw);
  margin: 0 auto;
  padding: 0 clamp(1rem, 3vw, 2rem);
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 3rem;
  
  @media (min-width: 900px) {
    grid-template-columns: 2fr 1fr;
    gap: 4rem;
  }
`;

const MainContent = styled.div`
  font-family: 'Inter', sans-serif;
  
  p {
    font-size: 1.1rem;
    line-height: 1.7;
    color: #333;
    margin-bottom: 1.5rem;
  }
  
  strong {
    color: #222;
    font-weight: 600;
  }
  
  ul {
    margin: 1rem 0 2rem 0;
    padding-left: 0;
    list-style: none;
  }
  
  li {
    background: #fff;
    margin-bottom: 0.8rem;
    padding: 1rem 1.5rem;
    border-radius: 8px;
    border-left: 4px solid ${theme.colors.accent};
    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
    font-size: 1rem;
    color: #333;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
    
    &:hover {
      transform: translateX(4px);
      box-shadow: 0 4px 12px ${theme.colors.accent}26;
    }
  }
`;

const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const InfoCard = styled.div`
  background: #fff;
  padding: 2rem;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  border: 1px solid #f0f0f0;
`;

const InfoTitle = styled.h3`
  font-family: 'Playfair Display', serif;
  font-size: 1.5rem;
  color: ${theme.colors.accent};
  margin-bottom: 1rem;
  font-weight: 700;
`;

const BulletList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  
  li {
    background: ${theme.colors.sectionAlt};
    margin-bottom: 0.5rem;
    padding: 0.8rem 1rem;
    border-radius: 6px;
    font-size: 0.95rem;
    color: #444;
    border-left: 3px solid ${theme.colors.accent};
  }
`;

const BackButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: linear-gradient(135deg, ${theme.colors.accentSoft} 0%, ${theme.colors.accent} 100%);
  color: #1F2937;
  text-decoration: none;
  padding: 1rem 2rem;
  border-radius: 12px;
  font-family: 'Inter', sans-serif;
  font-size: 1rem;
  font-weight: 600;
  transition: all 0.3s ease;
  box-shadow: 0 4px 16px ${theme.colors.accent}4d;
  margin-top: 2rem;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px ${theme.colors.accent}66;
    background: linear-gradient(135deg, ${theme.colors.accent} 0%, ${theme.colors.accentSoft} 100%);
  }
  
  span {
    font-size: 1.2rem;
  }
`;

const SectorDetailPage: React.FC = () => {
  const { slug } = useParams();
  const location = useLocation();
  
  // Check both sectorsData and serviceSectors for the sector
  const sector = sectorsData.find(s => s.slug === slug) || serviceSectors.find(s => s.slug === slug);

  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // Smart back: if navigated from home, go home; else, go to services
  const from = location.state?.from === '/' ? '/' : '/services';

  if (!sector) {
    return (
      <ContentSection>
        <ContentContainer style={{ textAlign: 'center' }}>
          <Heading level="h1" align="center">Sector Not Found</Heading>
          <p>The sector you're looking for doesn't exist.</p>
          <BackButton to="/">
            <span>←</span> Back to Home
          </BackButton>
        </ContentContainer>
      </ContentSection>
    );
  }

  const bannerImage = (sector as any).banner || sector.image;

  return (
    <>
      <SectorHero>
        <SectorHeroBg bgImage={bannerImage} />
        <SectorHeroOverlay />
        <SectorHeroContent>
          <SectorTitle>{sector.title}</SectorTitle>
          <SectorSubtitle>{sector.shortDesc}</SectorSubtitle>
        </SectorHeroContent>
      </SectorHero>

      <ContentSection>
        <ContentContainer>
          <ContentGrid>
            <MainContent>
              <div dangerouslySetInnerHTML={{ __html: sector.fullDesc }} />
              <BackButton to={from}>
                <span>←</span> Back to Sectors
              </BackButton>
            </MainContent>
            
            <Sidebar>
              <InfoCard>
                <InfoTitle>Key Roles We Fill</InfoTitle>
                <BulletList>
                  {sector.bullets.map((bullet, i) => (
                    <li key={i}>{bullet}</li>
                  ))}
                </BulletList>
              </InfoCard>
              
              <InfoCard>
                <InfoTitle>Quick Facts</InfoTitle>
                <BulletList>
                  <li>✓ 15+ Years Industry Experience</li>
                  <li>✓ Global Talent Network</li>
                  <li>✓ 48-Hour Initial Response</li>
                  <li>✓ Rigorous Screening Process</li>
                </BulletList>
              </InfoCard>
              
              <InfoCard>
                <InfoTitle>Ready to Get Started?</InfoTitle>
                <p style={{ marginBottom: '1.5rem', color: '#666', fontSize: '0.95rem' }}>
                  Connect with our specialists to discuss your staffing needs.
                </p>
                <BackButton to="/contact" style={{ margin: 0, fontSize: '0.9rem', padding: '0.8rem 1.5rem' }}>
                  Get In Touch →
                </BackButton>
              </InfoCard>
            </Sidebar>
          </ContentGrid>
        </ContentContainer>
      </ContentSection>
    </>
  );
};

export default SectorDetailPage;